import Axios, { AxiosResponse } from 'axios'
import { useQuery, UseQueryOptions } from 'react-query'

import { Listing as ListingResponse, Marker as MarkerResponse } from '@/types/nika'
import logger, { LogChannel } from '@/utils/logger'

const API_BASE_URL: string = process.env.NEXT_PUBLIC_NIKA_API_ENDPOINT!

const logChannel: LogChannel = 'lisa.http.nika'

export const MAX_PAGE_SIZE = 50

export type PaginationQueryParam = {
  page: number
  page_size: number
}

export type SortByQueryParam = string | string[]

export type WhereQueryParam = Record<string, unknown>

type ResponseMeta = {
  has_more: boolean
  object: 'list'
  total_objects: number
}

export type IndexResponse<T> = {
  data: T[]
  meta: ResponseMeta
}

const axios = Axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

export type ListingsResponse = {
  data: ListingResponse[]
  meta: {
    has_more: boolean
    object: string
    total_objects: number
  }
}

export function reqListings(
  filters?: WhereQueryParam,
  pagination?: PaginationQueryParam,
  sort_by?: SortByQueryParam
): Promise<AxiosResponse<ListingsResponse>> {
  const params: Record<string, any> = {
    page: pagination ? pagination.page : 1,
  }
  if (pagination && pagination.page_size) {
    params.page_size = pagination?.page_size
  }
  if (sort_by) {
    params.sort_by = sort_by
    if (Array.isArray(params.sort_by)) {
      params.sort_by = params.sort_by.join(',')
    }
  }
  if (filters) {
    params.where = JSON.stringify(filters)
  }

  return axios.get('/v2/listings/published', { params })
}

export function reqListing(id: string): Promise<AxiosResponse<ListingResponse>> {
  return axios.get(`/v2/listings/published/${id}`)
}

export function reqMarkers(filters?: WhereQueryParam, zoom?: number) {
  const params: Record<string, any> = { zoom }
  if (filters) {
    params.where = JSON.stringify(filters)
  }

  return axios.get('/v2/listings/published/map/markers', { params })
}

export async function getListing(uuid: string): Promise<ListingResponse> {
  try {
    const { data } = await reqListing(uuid)
    return data
  } catch (e) {
    logger.error({ channel: logChannel }, `getListing ${uuid} - ${e}`)
    throw e
  }
}

function buildQueryKeyForIndex(
  name: string,
  filters?: WhereQueryParam,
  pagination?: PaginationQueryParam,
  sort_by?: SortByQueryParam
) {
  return [
    name,
    sort_by || '-updated_at',
    pagination ? pagination.page_size : '20',
    pagination ? pagination.page : '1',
    filters ? JSON.stringify(filters) : '{}',
  ]
}

export function GetListings(
  filters?: WhereQueryParam,
  pagination?: PaginationQueryParam,
  sort_by?: SortByQueryParam,
  options?: UseQueryOptions<IndexResponse<ListingResponse>>
) {
  let key = buildQueryKeyForIndex('listings-index', filters, pagination, sort_by)
  return useQuery(
    key,
    () => {
      const inner = async () => {
        try {
          let { data } = await reqListings(filters, pagination, sort_by)
          return data
        } catch (e) {
          logger.error({ channel: logChannel }, `reqListings - ${e}`)
          throw e
        }
      }
      return inner()
    },
    options
  )
}

function GetMarkers(
  filters?: WhereQueryParam,
  zoom?: number,
  options?: UseQueryOptions<IndexResponse<MarkerResponse>>
) {
  let key = buildQueryKeyForIndex('markers-index', filters, { page: zoom, page_size: 0 }, null)
  return useQuery(
    key,
    () => {
      const inner = async () => {
        try {
          if (!filters) {
            return { data: [], meta: {} }
          }
          let { data } = await reqMarkers(filters, zoom)
          return data
        } catch (e) {
          logger.error({ channel: logChannel }, `reqMarkers - ${e}`)
          throw e
        }
      }
      return inner()
    },
    options
  )
}

export function GetMapElements(
  maxZoom: number,
  filters?: WhereQueryParam,
  zoom?: number,
  options?: UseQueryOptions<IndexResponse<MarkerResponse | ListingResponse>>
) {
  if (zoom === maxZoom) {
    return GetListings(
      filters,
      { page: 1, page_size: 50 },
      null,
      options as UseQueryOptions<IndexResponse<ListingResponse>>
    )
  }
  return GetMarkers(filters, zoom, options as UseQueryOptions<IndexResponse<MarkerResponse>>)
}
